import React, { Suspense, lazy, useLayoutEffect, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { Toaster } from "sonner";
import StructuredData from "@/components/seo/StructuredData";
import { SITE } from "@/config/site";
import { captureLeadEntry } from "@/lib/leadAttribution";
import { emitConversion } from "@/lib/conversionEvents";

const Landing = lazy(() => import("@/pages/Landing"));
const Admin = lazy(() => import("@/pages/Admin"));
const Oferta = lazy(() => import("@/pages/Oferta"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const StronyInternetowe = lazy(() => import("@/pages/uslugi/StronyInternetowe"));
const Seo = lazy(() => import("@/pages/uslugi/Seo"));
const Geo = lazy(() => import("@/pages/uslugi/Geo"));
const Reklamy = lazy(() => import("@/pages/uslugi/Reklamy"));
const ReklamyUgc = lazy(() => import("@/pages/uslugi/ReklamyUgc"));
const ReklamyUgcGaleria = lazy(() => import("@/pages/uslugi/ReklamyUgcGaleria"));
const It = lazy(() => import("@/pages/uslugi/It"));
const AutomatyzacjeN8n = lazy(() => import("@/pages/uslugi/AutomatyzacjeN8n"));
const AutomatyzacjeN8nPrzyklady = lazy(() => import("@/pages/uslugi/AutomatyzacjeN8nPrzyklady"));
const RaportyAnalizyBiznesowe = lazy(() => import("@/pages/uslugi/RaportyAnalizyBiznesowe"));
const Realizacje = lazy(() => import("@/pages/Realizacje"));
const ONas = lazy(() => import("@/pages/ONas"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Kontakt = lazy(() => import("@/pages/Kontakt"));
const Regulamin = lazy(() => import("@/pages/Regulamin"));
const SprawdzWidocznosc = lazy(() => import("@/pages/SprawdzWidocznosc"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
      <div className="w-8 h-8 rounded-full border-2 border-zinc-700 border-t-[var(--brand-accent)] animate-spin" />
    </div>
  );
}

function ScrollToTop() {
  const location = useLocation();
  useLayoutEffect(() => {
    window.history.scrollRestoration = "manual";
    if (!location.hash) window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname, location.hash]);
  return null;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/uslugi/strony-internetowe" element={<StronyInternetowe />} />
        <Route path="/uslugi/seo" element={<Seo />} />
        <Route path="/uslugi/geo" element={<Geo />} />
        <Route path="/uslugi/reklamy" element={<Reklamy />} />
        <Route path="/uslugi/reklamy-ugc" element={<ReklamyUgc />} />
        <Route path="/uslugi/reklamy-ugc/galeria" element={<ReklamyUgcGaleria />} />
        <Route path="/uslugi/it" element={<It />} />
        <Route path="/uslugi/automatyzacje-n8n" element={<AutomatyzacjeN8n />} />
        <Route path="/uslugi/automatyzacje-n8n/przyklady-agentow-ai" element={<AutomatyzacjeN8nPrzyklady />} />
        <Route path="/uslugi/raporty-i-analizy-biznesowe" element={<RaportyAnalizyBiznesowe />} />
        <Route path="/uslugi/data-science" element={<RaportyAnalizyBiznesowe />} />
        <Route path="/uslugi/analiza-danych" element={<RaportyAnalizyBiznesowe />} />
        <Route path="/realizacje" element={<Realizacje />} />
        <Route path="/o-nas" element={<ONas />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/kontakt" element={<Kontakt />} />
        <Route path="/sprawdz-widocznosc" element={<SprawdzWidocznosc />} />
        <Route path="/oferta" element={<Oferta />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/login" element={<Admin />} />
        <Route path="/polityka-prywatnosci" element={<PrivacyPolicy />} />
        <Route path="/regulamin" element={<Regulamin />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App({ Router = BrowserRouter, routerProps = {} }) {
  useEffect(captureLeadEntry, []);
  const localBusinessSchema = {
    "@type": ["Organization", "LocalBusiness"],
    "@id": `${SITE.domain}/#organization`,
    name: SITE.name,
    legalName: SITE.legalName,
    url: SITE.domain,
    telephone: SITE.phone,
    email: SITE.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.address.street,
      postalCode: SITE.address.postalCode,
      addressLocality: SITE.address.city,
      addressRegion: SITE.address.region,
      addressCountry: SITE.address.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: SITE.geo.lat,
      longitude: SITE.geo.lng,
    },
    areaServed: "PL",
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: SITE.openingHours.opens,
      closes: SITE.openingHours.closes,
    },
    image: `${SITE.domain}/logo.svg`,
  };

  return (
    <div className="App" onClick={(event) => {
      const href = event.target.closest?.("a")?.getAttribute("href") || "";
      if (href.startsWith("tel:")) emitConversion("contact_phone_click");
      if (href.startsWith("mailto:")) emitConversion("contact_email_click");
    }}>
      <MotionConfig reducedMotion="user">
        <StructuredData id="app-organization" data={localBusinessSchema} />
        <Router {...routerProps}>
          <ScrollToTop />
          <AppRoutes />
        </Router>
        <Toaster position="bottom-right" richColors closeButton />
      </MotionConfig>
    </div>
  );
}

export default App;
